/**
 * Updates only the hostname prefix on an existing Greenhouse OAuth integration.
 *
 * The hook sends the typed request to the dedicated integration RPC. The server changes the two
 * stored JSON copies without running integration hooks. GreenhouseOAuthCard and IntegrationSettings
 * call the shared submit helper after their panels parse the input.
 */

import { useMutation } from 'react-query';

import { getExternalErrorMessage } from '@modernloop/shared/utils';

import type { GreenhouseSubdomain } from 'src/views-new/Settings/Integrations/GreenhouseSubdomain';

import { ApiVerb, apiPost } from 'src/utils/api';

/** Request sent to the Greenhouse OAuth subdomain update endpoint. */
export interface UpdateGreenhouseOauthSubdomainRequest {
  greenhouse_subdomain: string;
}

/** Result union returned by the Greenhouse OAuth subdomain update endpoint. */
export interface UpdateGreenhouseOauthSubdomainResponse {
  success?: Record<string, never>;
  invalid_subdomain?: Record<string, never>;
  not_found?: Record<string, never>;
  unknown?: { message?: string };
}

/** Mutation contract used by the shared Greenhouse update submission helper. */
export interface GreenhouseOauthSubdomainUpdateMutation {
  mutateAsync: (request: UpdateGreenhouseOauthSubdomainRequest) => Promise<UpdateGreenhouseOauthSubdomainResponse>;
}

/** Sends a typed Greenhouse OAuth subdomain update request. */
async function updateGreenhouseOauthSubdomain(
  request: UpdateGreenhouseOauthSubdomainRequest
): Promise<UpdateGreenhouseOauthSubdomainResponse> {
  const response = await apiPost<UpdateGreenhouseOauthSubdomainRequest, UpdateGreenhouseOauthSubdomainResponse>(
    ApiVerb.UPDATE_GREENHOUSE_OAUTH_SUBDOMAIN,
    request
  );
  return response.data;
}

/** Returns the targeted Greenhouse OAuth subdomain mutation. */
export default function useGreenhouseOauthSubdomainUpdate() {
  return useMutation(updateGreenhouseOauthSubdomain);
}

/**
 * Submits a connected Greenhouse subdomain update and keeps its result handling in one place.
 *
 * 1. Send the parsed subdomain to the targeted endpoint.
 * 2. Refresh the caller's integration query after a success or missing integration.
 * 3. Run the caller callback for the result and show the existing generic or server error.
 */
export async function submitGreenhouseOauthSubdomainUpdate({
  subdomain,
  updateSubdomain,
  refetch,
  onSuccess,
  onNotFound = onSuccess,
  enqueueSnackbar,
  genericErrorMessage,
}: {
  subdomain: GreenhouseSubdomain;
  updateSubdomain: GreenhouseOauthSubdomainUpdateMutation;
  refetch: () => Promise<unknown>;
  onSuccess?: () => void;
  onNotFound?: () => void;
  enqueueSnackbar: (message: string, options: { variant: 'error' }) => void;
  genericErrorMessage: string;
}): Promise<void> {
  try {
    const result = await updateSubdomain.mutateAsync({ greenhouse_subdomain: subdomain });
    if (result.success) {
      await refetch();
      onSuccess?.();
      return;
    }
    if (result.not_found) {
      await refetch();
      onNotFound?.();
      return;
    }
    if (result.invalid_subdomain) {
      throw new Error();
    }
    if (result.unknown) {
      throw new Error(result.unknown.message);
    }
    throw new Error();
  } catch (error) {
    enqueueSnackbar(getExternalErrorMessage(error) || genericErrorMessage, { variant: 'error' });
  }
}
